/* ─────────────────────────────────────────────────────────────
   MoneyField — a decimal amount held as a STRING (ENT-565 FR-67).

   The value goes in and comes out exactly as typed: no parsing to a JS number, no rounding, no
   formatting, no arithmetic. A float cannot hold 0.10 or 1500.50 exactly, and a figure a person
   typed must reach the server as the characters they typed. The only judgement made here is
   whether the characters are a plain decimal (digits, at most two decimals), and that is a
   validation message, never a rewrite.

   Config (field.props):
   - currency (string, optional) — a currency code shown beside the amount. Display only; the value
     never contains it.
   ───────────────────────────────────────────────────────────── */

import type { FieldDisplayProps, FieldRendererProps } from '../../config/types';
import { Input } from '@/components/ui/Input';
import { Text } from '@/components/ui/Text';

export interface MoneyControlProps {
  id?: string;
  value: unknown;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  currency?: string;
  placeholder?: string;
  ariaLabelledBy?: string;
}

/** The bare control, shared by the top-level renderer and the list builder's sub-fields. */
export function MoneyControl({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  invalid,
  currency,
  placeholder,
  ariaLabelledBy,
}: MoneyControlProps) {
  const text = typeof value === 'string' ? value : value == null ? '' : String(value);
  const input = (
    <Input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={text}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={invalid || undefined}
      aria-labelledby={ariaLabelledBy}
      className="text-right font-mono-face tabular-nums"
    />
  );
  if (!currency) return input;
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">{input}</div>
      <Text variant="body-sm" color="muted" as="span" data-testid="money-currency">
        {currency}
      </Text>
    </div>
  );
}

export function MoneyFieldRenderer({ field, value, onChange, onBlur, error }: FieldRendererProps) {
  const currency = typeof field.props?.currency === 'string' ? field.props.currency : undefined;
  return (
    <MoneyControl
      id={field.id}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={field.isDisabled}
      invalid={!!error}
      currency={currency}
      placeholder={field.placeholder}
    />
  );
}

export function MoneyDisplay({ field, value }: FieldDisplayProps) {
  const text = typeof value === 'string' ? value : value == null ? '' : String(value);
  if (text === '') return <Text variant="body-md" color="muted">—</Text>;
  const currency = typeof field.props?.currency === 'string' ? field.props.currency : '';
  return (
    <Text variant="body-md" mono>
      {currency ? `${text} ${currency}` : text}
    </Text>
  );
}
